import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import useSeo from "../lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function Insights() {
  useSeo({
    title: "Insights · BuildOnce Studio",
    description:
      "Short reads on clarity, positioning, and where AI belongs in a founder-led business. Notes from BuildOnce Studio.",
  });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/posts`)
      .then((r) => active && setPosts(r.data || []))
      .catch(() => active && setPosts([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div data-testid="insights-page">
      <section className="border-b border-bos-ice">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-20 lg:pt-24 pb-20">
          <p className="bos-eyebrow">Insights</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-medium text-bos-navy tracking-tight leading-[1.08] max-w-3xl">
            Notes from the studio on <span className="text-bos-primary">clarity, positioning, and AI.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[17px] text-bos-charcoal/85 leading-[1.75]">
            Short reads on the questions founders bring us most often, what
            unclear positioning actually costs, where AI belongs in your
            business, and what it really means to build once.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
        {loading ? (
          <div data-testid="insights-loading" className="text-bos-charcoal/60">Loading…</div>
        ) : posts.length === 0 ? (
          <div data-testid="insights-empty" className="text-bos-charcoal/60">No posts yet. Check back soon.</div>
        ) : (
          <ul className="grid gap-10" data-testid="insights-list">
            {posts.map((p, i) => (
              <li
                key={p.slug}
                data-testid={`insight-card-${p.slug}`}
                className="border-b border-bos-ice pb-10"
              >
                <Link to={`/insights/${p.slug}`} className="group block">
                  <div className="grid lg:grid-cols-[1fr_3fr] gap-8 items-start">
                    <div className="text-[12px] tracking-[0.16em] uppercase text-bos-mid">
                      {String(i + 1).padStart(2, "0")} · {p.category}
                    </div>
                    <div>
                      <h2 className="text-[26px] lg:text-[32px] font-medium text-bos-navy tracking-tight leading-[1.15] group-hover:text-bos-primary transition-colors">
                        {p.title}
                      </h2>
                      <p className="mt-4 text-[15px] text-bos-charcoal/80 leading-[1.75] max-w-2xl">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 flex items-center gap-5 text-[12px] text-bos-charcoal/60">
                        <span>{formatDate(p.published_at)}</span>
                        <span>{p.read_minutes} min read</span>
                        <span className="inline-flex items-center gap-1.5 text-bos-primary">
                          Read
                          <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
